import { CovanceFilter } from './../../covance-filter/covance-filter';
import { Settings } from '../../core/settings';
import { FilterFieldValue } from '../../covance-filter/covance-filter.models';
import { SponsorRouteData } from '../sponsor-route-data';
import { UrlBuilder } from '../../core/url-builder/url-builder';
import { ActivatedRoute } from '@angular/router';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { Observable } from 'rxjs';
import { TabCategory } from '../../documents';
import { LabTestIdentity } from './lab-test';
import { map, refCount, publishReplay, combineLatest, distinctUntilChanged, filter } from 'rxjs/operators';
import { LabResultsFilterStorage } from './lab-results-filter-storage';
import { LabTestsIdentitiesStorage } from './lab-tests-identities-storage';
import { LabResultsDataStorage } from './lab-results-data-storage';
import { routeParams, findStudySiteIdentities, findProtocolId } from '../../shared/route-helpers';
import { trendsSiteFilterTitle } from '../../on-screen-results/standard-test-results/on-screen-results-filter-defenitions';

@Component({
    selector: 'sponsor-trend-analysis',
    template: require('./sponsor-trend-analysis.component.html'),
    styles: [require('./sponsor-trend-analysis.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [LabResultsDataStorage]
})
export class SponsorTrendAnalysisComponent {

    public tabs: Observable<TabCategory[]>;

    public filterDefinitions: Observable<FilterFieldValue[]>;
    public selectedTest: Observable<LabTestIdentity>;
    public tests: Observable<LabTestIdentity[]>;
    public protocolId: number;

    constructor(
        private urlBuilder: UrlBuilder,
        private routeData: SponsorRouteData,
        private route: ActivatedRoute,
        labTestsIdentitiesStorage: LabTestsIdentitiesStorage,
        filterStorage: LabResultsFilterStorage,
        private settings: Settings,
    ) {
        this.protocolId = findProtocolId(this.route.snapshot);
        const siteIdentities = findStudySiteIdentities(this.route.snapshot).map(f => ({key: f.siteNumber, value: f}));
        this.filterDefinitions = filterStorage.trendsFilterFields.pipe(
            map(fd => {
                const siteFilter = fd.find(f => f.def.title === trendsSiteFilterTitle);
                if (siteFilter) {
                    siteFilter.def.getItems = () => siteIdentities;
                }
                return fd;
            })
        );

        this.tests = labTestsIdentitiesStorage.labTestsIdentities;
        this.selectedTest = this.route.params.pipe(
            map(params => params[routeParams.zcTestCode] as string),
            distinctUntilChanged(),
            combineLatest(this.tests),
            map(([zcTestId, tests]) => {
                const selTest = tests.find(t => t.zcTestCode === zcTestId);
                if (!selTest) {
                    this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(this.routeData.snapshot.selectedStudy)
                        .trendAnalysis(new LabTestIdentity() || null)
                        .navigate({ navigationExtras: { replaceUrl: true } });
                }
                return selTest;
            }),
            filter(t => !!t),
            publishReplay(1),
            refCount());

        if (this.selectedTest) {
            this.tabs = this.selectedTest.pipe(
                map(selectedTest => {
                    const tabs: TabCategory[] = [];
                    if (this.settings.isFeatureEnabled('sponsorBoxPlots')) {
                        const siteBoxPlotUrl = this.urlBuilder
                            .esa()
                            .sponsor()
                            .study(this.routeData.snapshot.selectedStudy)
                            .trendAnalysis(selectedTest)
                            .siteBoxPlot()
                            .url();

                        const visitBoxPlotUrl = this.urlBuilder
                            .esa()
                            .sponsor()
                            .study(this.routeData.snapshot.selectedStudy)
                            .trendAnalysis(selectedTest)
                            .visitBoxPlot()
                            .url();

                        tabs.push({ name: 'Site BoxPlot', url: siteBoxPlotUrl });
                        tabs.push({ name: 'Visit BoxPlot', url: visitBoxPlotUrl });
                    }

                    const trendsUrl = this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(this.routeData.snapshot.selectedStudy)
                        .trendAnalysis(selectedTest)
                        .trends()
                        .url();

                    tabs.push({ name: 'Trends', url: trendsUrl });

                    return tabs;
                }),
                publishReplay(1),
                refCount());
        }
        }

    public onTestChanged(test: LabTestIdentity[]) {
        this.selectedTest = this.route.params.pipe(
        map(params => params[routeParams.zcTestCode] as string),
        distinctUntilChanged(),
        combineLatest(this.tests),
        map(([zcTestId, tests]) => {
            const selTest = tests.find(t => t.zcTestCode === zcTestId);
            if (!selTest) {
                this.urlBuilder
                    .esa()
                    .sponsor()
                    .study(this.routeData.snapshot.selectedStudy)
                    .trendAnalysis(tests[0] || null)
                    .navigate({ navigationExtras: { replaceUrl: true } });
            }
            return selTest;
        }),
        filter(t => !!t),
        publishReplay(1),
        refCount());

        this.tabs = this.selectedTest.pipe(
            map(selectedTest => {
                const tabs: TabCategory[] = [];
                if (this.settings.isFeatureEnabled('sponsorBoxPlots')) {
                    const siteBoxPlotUrl = this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(this.routeData.snapshot.selectedStudy)
                        .trendAnalysis(selectedTest)
                        .siteBoxPlot()
                        .url();

                    const visitBoxPlotUrl = this.urlBuilder
                        .esa()
                        .sponsor()
                        .study(this.routeData.snapshot.selectedStudy)
                        .trendAnalysis(selectedTest)
                        .visitBoxPlot()
                        .url();

                    tabs.push({ name: 'Site BoxPlot', url: siteBoxPlotUrl });
                    tabs.push({ name: 'Visit BoxPlot', url: visitBoxPlotUrl });
                }

                const trendsUrl = this.urlBuilder
                    .esa()
                    .sponsor()
                    .study(this.routeData.snapshot.selectedStudy)
                    .trendAnalysis(selectedTest)
                    .trends()
                    .url();

                tabs.push({ name: 'Trends', url: trendsUrl });

                return tabs;
            }),
            publishReplay(1),
            refCount());
        this.urlBuilder
            .esa()
            .sponsor()
            .study(this.routeData.snapshot.selectedStudy)
            .trendAnalysis(test[0])
            .navigate({ preserveQueryParams: true, preserveFragments: true });
    }
}
